// Editorial content schema for Grup Xaluca blog posts.
// All copy is trilingual (EN/FR/ES) via { en, fr, es } dicts.
//
// Schema:
//   id            stable identifier (used in routes + keys)
//   slug          URL slug (kebab-case)
//   category      one of "morocco" | "hotels" | "events" | "bivouacs" | "people"
//   title         { en, fr, es }
//   excerpt       { en, fr, es }
//   author        { name, role: { en, fr, es } }
//   publishedAt   ISO-8601 date string
//   readingTime   minutes (number)
//   tags          string[]
//   featuredImage absolute URL to a 1600w+ image
//   tone          "dark" | "earth" | "gold" — used to vary card accents

export const BLOG_CATEGORIES = [
  {
    key: "morocco",
    label: { en: "Morocco", fr: "Maroc", es: "Marruecos" },
    blurb: {
      en: "Stories from the wider south — souks, valleys, oases, festivals.",
      fr: "Récits du grand sud — souks, vallées, oasis, festivals.",
      es: "Historias del gran sur — zocos, valles, oasis, festivales.",
    },
    to: "/southern-morocco",
    image: "https://images.unsplash.com/photo-1531786208860-83b16b9a7fa9?auto=format&fit=crop&w=1800&q=80",
  },
  {
    key: "hotels",
    label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" },
    blurb: {
      en: "Behind the kasbahs, the maisons d'hôte and the refuges.",
      fr: "Dans les coulisses des kasbahs, maisons d'hôte et refuges.",
      es: "Tras las kasbahs, las casas de huéspedes y los refugios.",
    },
    to: "/hotels",
    image: "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1800&q=80",
  },
  {
    key: "events",
    label: { en: "Events", fr: "Événements", es: "Eventos" },
    blurb: {
      en: "Festivals, weddings, sporting and brand events in the dunes.",
      fr: "Festivals, mariages, événements sportifs et de marque dans les dunes.",
      es: "Festivales, bodas, eventos deportivos y de marca en las dunas.",
    },
    to: "/events",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1800&q=80",
  },
  {
    key: "bivouacs",
    label: { en: "Bivouacs", fr: "Bivouacs", es: "Vivacs" },
    blurb: {
      en: "Camps under the Milky Way, from the traditional to the luxurious.",
      fr: "Camps sous la Voie lactée, du traditionnel au luxueux.",
      es: "Campamentos bajo la Vía Láctea, del tradicional al lujoso.",
    },
    to: "/bivouacs",
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1800&q=80",
  },
];

export const BLOG_TEAM = [
  {
    id: "tayeb",
    name: "Tayeb Ettaiek",
    role: { en: "Co-founder · Erfoud", fr: "Co-fondateur · Erfoud", es: "Cofundador · Erfoud" },
    quote: {
      en: "Hospitality is not a service. It is a kind of love that you give before you have been asked.",
      fr: "L'hospitalité n'est pas un service. C'est une forme d'amour que l'on donne avant qu'on ne le demande.",
      es: "La hospitalidad no es un servicio. Es una forma de amor que se da antes de que la pidan.",
    },
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "lluis",
    name: "Lluís Pont",
    role: { en: "Co-founder · Barcelona", fr: "Co-fondateur · Barcelone", es: "Cofundador · Barcelona" },
    quote: {
      en: "We build the way the south builds — slowly, with what is at hand, for a long time.",
      fr: "Nous bâtissons à la manière du sud — lentement, avec ce qui est à portée de main, pour longtemps.",
      es: "Construimos como construye el sur — lentamente, con lo que hay a mano, para mucho tiempo.",
    },
    photo: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "khadija",
    name: "Khadija Ait Ali",
    role: { en: "Director · Hotel Xaluca Midelt", fr: "Directrice · Hôtel Xaluca Midelt", es: "Directora · Hotel Xaluca Midelt" },
    quote: {
      en: "Our walls are forty centimetres thick — that is how the mountain wants us to live.",
      fr: "Nos murs font quarante centimètres d'épaisseur — c'est ainsi que la montagne veut que nous vivions.",
      es: "Nuestros muros tienen cuarenta centímetros — así es como la montaña quiere que vivamos.",
    },
    photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "lahcen",
    name: "Lahcen El Madouri",
    role: { en: "Director · Aït Ben Haddou", fr: "Directeur · Aït Ben Haddou", es: "Director · Aït Ben Haddou" },
    quote: {
      en: "Heritage is not protected by being closed off. It is protected by being lived in.",
      fr: "Le patrimoine ne se protège pas en se fermant. Il se protège en se vivant.",
      es: "El patrimonio no se protege cerrándolo. Se protege habitándolo.",
    },
    photo: "https://images.unsplash.com/photo-1542327897-4141c5cda6c1?auto=format&fit=crop&w=900&q=80",
  },
];

export const BLOG_TESTIMONIALS = [
  {
    id: "marina-2024",
    quote: {
      en: "Three nights and I have not stopped thinking about the silence of the courtyard. The food, the people, the dunes — but the silence above all.",
      fr: "Trois nuits, et je n'ai pas cessé de penser au silence de la cour. La cuisine, les gens, les dunes — mais le silence avant tout.",
      es: "Tres noches, y no he dejado de pensar en el silencio del patio. La comida, la gente, las dunas — pero el silencio sobre todo.",
    },
    author: "Marina B.",
    origin: { en: "Milan", fr: "Milan", es: "Milán" },
    stay: { en: "Kasbah Hotel Xaluca Arfoud, March 2024", fr: "Kasbah Hotel Xaluca Arfoud, mars 2024", es: "Kasbah Hotel Xaluca Arfoud, marzo 2024" },
  },
  {
    id: "anders-2024",
    quote: {
      en: "We arrived skeptical of the \"luxury desert camp\" idea. We left having spent the most considered night of any trip we have ever taken.",
      fr: "Nous sommes arrivés sceptiques face à l'idée d'un « camp de luxe ». Nous sommes repartis après la nuit la plus pensée d'aucun de nos voyages.",
      es: "Llegamos escépticos ante la idea del «campamento de lujo». Nos fuimos tras la noche más cuidada de cualquier viaje que hayamos hecho.",
    },
    author: "Anders V.",
    origin: { en: "Copenhagen", fr: "Copenhague", es: "Copenhague" },
    stay: { en: "La Belle Étoile Luxury Camp, January 2024", fr: "La Belle Étoile Luxury Camp, janvier 2024", es: "La Belle Étoile Luxury Camp, enero 2024" },
  },
  {
    id: "sofia-2023",
    quote: {
      en: "I came back home with a recipe for harira, a kilim that took me a morning to choose, and a friendship with the night watchman that has outlasted the trip.",
      fr: "Je suis rentrée avec une recette de harira, un kilim que j'ai mis une matinée à choisir, et une amitié avec le gardien de nuit qui a survécu au voyage.",
      es: "Volví a casa con una receta de harira, un kilim que tardé toda una mañana en elegir y una amistad con el vigilante nocturno que ha durado más que el viaje.",
    },
    author: "Sofia P.",
    origin: { en: "Lisbon", fr: "Lisbonne", es: "Lisboa" },
    stay: { en: "Hotel Xaluca Dades, October 2023", fr: "Hotel Xaluca Dades, octobre 2023", es: "Hotel Xaluca Dades, octubre 2023" },
  },
  {
    id: "rafael-2023",
    quote: {
      en: "The team turned a four-day brand off-site into a piece of theatre. The desert did half the work, but the family did the other half.",
      fr: "L'équipe a transformé un séminaire d'entreprise de quatre jours en une pièce de théâtre. Le désert a fait la moitié du travail ; la famille, l'autre moitié.",
      es: "El equipo convirtió un retiro de marca de cuatro días en una pieza de teatro. El desierto hizo la mitad del trabajo; la familia, la otra mitad.",
    },
    author: "Rafael M.",
    origin: { en: "Mexico City", fr: "Mexico", es: "Ciudad de México" },
    stay: { en: "La Belle Étoile Luxury Camp Mini, May 2023", fr: "La Belle Étoile Luxury Camp Mini, mai 2023", es: "La Belle Étoile Luxury Camp Mini, mayo 2023" },
  },
  {
    id: "yui-2023",
    quote: {
      en: "Aït Ben Haddou after dusk is a place I did not know existed — the cinema you grew up with, emptied of cameras and filled with stars.",
      fr: "Aït Ben Haddou après le crépuscule est un endroit dont j'ignorais l'existence — le cinéma de notre enfance, vidé des caméras et rempli d'étoiles.",
      es: "Aït Ben Haddou tras el crepúsculo es un lugar cuya existencia ignoraba — el cine de la infancia, vacío de cámaras y lleno de estrellas.",
    },
    author: "Yui T.",
    origin: { en: "Tokyo", fr: "Tokyo", es: "Tokio" },
    stay: { en: "Xaluca Aït Ben Haddou (preview night), 2023", fr: "Xaluca Aït Ben Haddou (nuit-avant-première), 2023", es: "Xaluca Aït Ben Haddou (noche previa), 2023" },
  },
  {
    id: "elena-2022",
    quote: {
      en: "Forty couples got married there last year, the concierge told me. I now understand exactly how. I would marry someone again just to do it.",
      fr: "Quarante couples s'y sont mariés l'an dernier, m'a dit le concierge. Je comprends maintenant pourquoi. Je me marierais à nouveau rien que pour y revenir.",
      es: "Cuarenta parejas se casaron allí el año pasado, me dijo el conserje. Ahora entiendo exactamente cómo. Volvería a casarme solo para repetirlo.",
    },
    author: "Elena & Marco F.",
    origin: { en: "Buenos Aires", fr: "Buenos Aires", es: "Buenos Aires" },
    stay: { en: "Wedding at Kasbah Hotel Xaluca Arfoud, October 2022", fr: "Mariage au Kasbah Hotel Xaluca Arfoud, octobre 2022", es: "Boda en Kasbah Hotel Xaluca Arfoud, octubre 2022" },
  },
];

export const BLOG_POSTS = [
  {
    id: "post-stargazing-erg-chebbi",
    slug: "stargazing-erg-chebbi",
    category: "bivouacs",
    publishedAt: "2024-12-08",
    readingTime: 7,
    tags: ["astronomy", "sahara", "night"],
    author: { name: "Brahim Ouhadou", role: { en: "Third-generation host", fr: "Hôte de troisième génération", es: "Anfitrión de tercera generación" } },
    title: {
      en: "Reading Orion from a kilim — a guide to stargazing in Erg Chebbi",
      fr: "Lire Orion depuis un kilim — guide d'astronomie sur l'Erg Chebbi",
      es: "Leer Orión desde un kilim — guía de astronomía en Erg Chebbi",
    },
    excerpt: {
      en: "Far from any street-lamp, the Sahara is one of the darkest places on the planet. Here is what to look for, when, and how.",
      fr: "Loin de tout lampadaire, le Sahara est l'un des lieux les plus sombres de la planète. Voici quoi regarder, quand, et comment.",
      es: "Lejos de cualquier farola, el Sáhara es uno de los lugares más oscuros del planeta. Aquí qué buscar, cuándo y cómo.",
    },
    featuredImage: "https://images.unsplash.com/photo-1532978879514-6cb1cebaaa11?auto=format&fit=crop&w=1800&q=80",
    tone: "dark",
  },
  {
    id: "post-tayeb-lluis",
    slug: "tayeb-and-lluis-thirty-five-years",
    category: "people",
    publishedAt: "2024-11-22",
    readingTime: 9,
    tags: ["founders", "interview", "history"],
    author: { name: "Editorial team", role: { en: "Grup Xaluca", fr: "Grup Xaluca", es: "Grup Xaluca" } },
    title: {
      en: "Tayeb and Lluís: thirty-five years of building at the edge of the desert",
      fr: "Tayeb et Lluís : trente-cinq ans à bâtir en bordure du désert",
      es: "Tayeb y Lluís: treinta y cinco años construyendo al borde del desierto",
    },
    excerpt: {
      en: "An interview with the two co-founders — on friendship, pisé, and the night a single lamp changed their idea of hospitality.",
      fr: "Entretien avec les deux co-fondateurs — sur l'amitié, le pisé, et la nuit où une seule lampe a changé leur idée de l'hospitalité.",
      es: "Entrevista con los dos cofundadores — sobre amistad, tapial y la noche en que una sola lámpara cambió su idea de hospitalidad.",
    },
    featuredImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1800&q=80",
    tone: "earth",
  },
  {
    id: "post-pise-craft",
    slug: "the-grammar-of-pise",
    category: "hotels",
    publishedAt: "2024-10-14",
    readingTime: 6,
    tags: ["architecture", "craft", "midelt"],
    author: { name: "Lluís Pont", role: { en: "Co-founder", fr: "Co-fondateur", es: "Cofundador" } },
    title: {
      en: "The grammar of pisé — Morocco's earthen architecture, in plain sight",
      fr: "La grammaire du pisé — l'architecture de terre marocaine, en pleine vue",
      es: "La gramática del tapial — la arquitectura de tierra marroquí, a plena vista",
    },
    excerpt: {
      en: "Why we keep building in earth — and what it costs, repays and demands.",
      fr: "Pourquoi nous bâtissons encore en pisé — ce que cela coûte, rend, et exige.",
      es: "Por qué seguimos construyendo en tapial — lo que cuesta, lo que devuelve, lo que exige.",
    },
    featuredImage: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1800&q=80",
    tone: "earth",
  },
  {
    id: "post-rissani-souk",
    slug: "before-the-souk-rissani-at-sunrise",
    category: "morocco",
    publishedAt: "2024-09-30",
    readingTime: 5,
    tags: ["souk", "rissani", "culture"],
    author: { name: "Editorial team", role: { en: "Grup Xaluca", fr: "Grup Xaluca", es: "Grup Xaluca" } },
    title: {
      en: "Before the tour buses: Rissani's Sunday souk at sunrise",
      fr: "Avant les autocars : le souk dominical de Rissani au lever du soleil",
      es: "Antes de los autobuses: el zoco dominical de Rissani al amanecer",
    },
    excerpt: {
      en: "One of the oldest dromedary markets in Morocco — what to see, what to buy, and what to leave alone.",
      fr: "L'un des plus anciens marchés aux dromadaires du Maroc — quoi voir, quoi acheter, et quoi laisser tranquille.",
      es: "Uno de los mercados de dromedarios más antiguos de Marruecos — qué ver, qué comprar y qué dejar en paz.",
    },
    featuredImage: "https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1800&q=80",
    tone: "gold",
  },
  {
    id: "post-wedding-mabroka",
    slug: "weddings-at-mabroka",
    category: "events",
    publishedAt: "2024-08-19",
    readingTime: 8,
    tags: ["weddings", "mabroka", "events"],
    author: { name: "Hassan Ouhadou", role: { en: "Head of camps & events", fr: "Chef des camps & événements", es: "Jefe de campamentos y eventos" } },
    title: {
      en: "Forty weddings under a tataoui ceiling — Mabroka, a year on",
      fr: "Quarante mariages sous un plafond de tataoui — Mabroka, un an plus tard",
      es: "Cuarenta bodas bajo un techo de tataoui — Mabroka, un año después",
    },
    excerpt: {
      en: "What we have learned about staging a Moroccan wedding for 120 people — and why we now think the desert does most of the work.",
      fr: "Ce que nous avons appris en organisant un mariage marocain pour 120 invités — et pourquoi nous pensons que le désert fait la majeure partie du travail.",
      es: "Lo que hemos aprendido organizando una boda marroquí para 120 invitados — y por qué creemos que el desierto hace la mayor parte del trabajo.",
    },
    featuredImage: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1800&q=80",
    tone: "gold",
  },
  {
    id: "post-mgoun",
    slug: "into-the-m-goun",
    category: "morocco",
    publishedAt: "2024-07-05",
    readingTime: 6,
    tags: ["m'goun", "trekking", "atlas"],
    author: { name: "Editorial team", role: { en: "Grup Xaluca", fr: "Grup Xaluca", es: "Grup Xaluca" } },
    title: {
      en: "Into the M'Goun: the four-day trek between canyon and summit",
      fr: "Au cœur du M'Goun : le trek de quatre jours entre canyon et sommet",
      es: "En el corazón del M'Goun: el trek de cuatro días entre cañón y cumbre",
    },
    excerpt: {
      en: "A practical, photograph-led guide to walking the M'Goun valley — with refuge nights, mule support and a Berber lunch on day three.",
      fr: "Un guide pratique et illustré du trek de la vallée du M'Goun — nuits en refuge, soutien muletier et déjeuner berbère au troisième jour.",
      es: "Una guía práctica e ilustrada del trek por el valle del M'Goun — noches en refugio, apoyo con mula y almuerzo bereber al tercer día.",
    },
    featuredImage: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1800&q=80",
    tone: "dark",
  },
  {
    id: "post-table-at-sijilmassa",
    slug: "the-table-at-sijilmassa",
    category: "hotels",
    publishedAt: "2024-06-12",
    readingTime: 5,
    tags: ["gastronomy", "sijilmassa", "arfoud"],
    author: { name: "Chef Anouar", role: { en: "Executive chef · Arfoud", fr: "Chef exécutif · Erfoud", es: "Chef ejecutivo · Erfoud" } },
    title: {
      en: "The table at Sijilmassa: a 12-hour mechoui and the recipe that never wrote itself down",
      fr: "La table de Sijilmassa : un méchoui de 12 heures et la recette qui ne s'est jamais écrite",
      es: "La mesa de Sijilmassa: un mechoui de 12 horas y la receta que nunca se escribió",
    },
    excerpt: {
      en: "Why our most ceremonial dish is also our quietest — and how a clay-oven dinner became the heart of the property.",
      fr: "Pourquoi notre plat le plus cérémoniel est aussi notre plus discret — et comment un dîner au four en argile est devenu le cœur de l'adresse.",
      es: "Por qué nuestro plato más ceremonial es también el más discreto — y cómo una cena en horno de barro se convirtió en el corazón de la propiedad.",
    },
    featuredImage: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1800&q=80",
    tone: "earth",
  },
  {
    id: "post-stories-from-tayeb",
    slug: "stories-from-tayeb",
    category: "people",
    publishedAt: "2024-05-04",
    readingTime: 4,
    tags: ["tayeb", "story"],
    author: { name: "Tayeb Ettaiek", role: { en: "Co-founder", fr: "Co-fondateur", es: "Cofundador" } },
    title: {
      en: "The night a generator failed and we ate by lantern — and the policy we kept ever after",
      fr: "La nuit où le groupe électrogène est tombé en panne — et la politique que nous avons gardée depuis",
      es: "La noche en que falló el generador y cenamos a la luz de un farol — y la política que mantuvimos desde entonces",
    },
    excerpt: {
      en: "A short story from the first year of the Arfoud kasbah — and the reason every dining room in the group keeps an oil lamp on the side table.",
      fr: "Une courte histoire de la première année de la kasbah d'Erfoud — et la raison pour laquelle chaque salle à manger du groupe garde une lampe à huile sur la table d'appoint.",
      es: "Una breve historia del primer año de la kasbah de Erfoud — y la razón por la que cada comedor del grupo tiene una lámpara de aceite en la mesa auxiliar.",
    },
    featuredImage: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1800&q=80",
    tone: "earth",
  },
];
